/* Add paint: find the colour by its code (or browse a make), choose system, size and quantity, add. The lines that
 * come out are the paint finder's own — same variant, same tier, same properties — so the price is the website's. */
import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { useApp } from '../lib/app-state.js';
import { searchCode, makes as loadMakes, makeColours, cleanCode, paintLines, colorTiers, finishOfFx, isTricoatFx, safeHex, systemFor, addonPlan, addonLine, systemKind, SYSTEM_LABEL, SYSTEM_NOTE } from '../lib/paint.js';
import { money, round2 } from '../lib/quote.js';

const FINISHES = ['Solid', 'Metallic', 'Pearl', 'Tricoat (3-stage)', 'Matte'];
const fxForFinish = (f) => ({ Solid: 's', Metallic: 'm', Pearl: 'p', 'Tricoat (3-stage)': 'pt', Matte: 'sk' })[f] || '';

export function Swatch({ hex, fx, name, size }) {
  const fin = isTricoatFx(fx, name) ? 'pearl' : finishOfFx(fx, name);
  return <span class={`swatch ${size === 'lg' ? 'lg' : ''} ${fin}`} style={{ background: safeHex(hex) }} />;
}

function ColourRow({ c, tier, onPick }) {
  const tri = isTricoatFx(c.fx, c.name);
  const fin = tri ? 'Tricoat' : finishOfFx(c.fx, c.name).replace(/^./, (x) => x.toUpperCase());
  return (
    <s-clickable onClick={() => onPick(c)} padding="small-200" borderRadius="base" accessibilityLabel={`${c.name} ${c.code}`}>
      <s-stack direction="inline" gap="base" alignItems="center">
        <Swatch hex={c.hex} fx={c.fx} name={c.name} />
        <s-stack gap="none">
          <s-text type="strong">{c.name}</s-text>
          <s-text color="subdued">
            {c.code}
            {c.make ? ` · ${c.make}` : ''}
            {c.uses?.length ? ` · ${c.uses.slice(0, 2).join(', ')}${c.uses.length > 2 ? '…' : ''}` : ''}
          </s-text>
        </s-stack>
        <s-badge tone={tri ? 'warning' : 'neutral'}>{fin}</s-badge>
        {tier !== 'Standard' && <s-badge tone="info">{tier}</s-badge>}
      </s-stack>
    </s-clickable>
  );
}

export default function PaintAdder({ onAdd, onClose }) {
  const { config, paint, addons, settings } = useApp();
  const base = config?.paintBase;
  const [code, setCode] = useState('');
  const [make, setMake] = useState('');
  const [makeList, setMakeList] = useState([]);
  const [results, setResults] = useState(null);
  const [filter, setFilter] = useState('');
  const [searching, setSearching] = useState(false);
  const [err, setErr] = useState('');
  const [color, setColor] = useState(null);
  const [manual, setManual] = useState(false);
  const [m, setM] = useState({ name: '', code: '', finish: 'Solid', tier: 'Standard', hex: '#8B8B8B' });
  const [choice, setChoice] = useState({ system: paint?.systems[0] || '', size: '', qty: 1, oz: 24, custom: false, vehicle: '' });
  const [prices, setPrices] = useState({}); // variantId → the price typed over the list price
  const [extras, setExtras] = useState({}); // add-on key → ticked (absent = the finder's default)
  const [extraQty, setExtraQty] = useState({}); // add-on key → quantity typed over the suggested one
  const [chosenAddon, setChosenAddon] = useState({}); // group → the product chosen among interchangeable ones
  const timer = useRef(0);

  useEffect(() => {
    if (!base) return;
    loadMakes(base).then((ms) => setMakeList([...ms].sort((a, b) => a.name.localeCompare(b.name)))).catch(() => {});
  }, [base]);

  useEffect(() => {
    if (!paint) return;
    const firstAerosol = paint.std.find((v) => v.kind === 'aerosol');
    setChoice((c) => ({ ...c, size: c.size || firstAerosol?.size || paint.sizes[0] }));
  }, [paint]);

  /* code typed → every make's colours under it; no code but a make → that make's colours */
  useEffect(() => {
    clearTimeout(timer.current);
    setErr('');
    if (!base) return;
    const q = code.trim();
    if (q.length < 2 && !make) { setResults(null); return; }
    timer.current = setTimeout(async () => {
      setSearching(true);
      try {
        if (q.length >= 2) {
          const makeName = makeList.find((x) => x.slug === make)?.name || '';
          setResults(await searchCode(base, q, { make: makeName }));
        } else {
          setResults(await makeColours(base, make));
        }
      } catch (e) {
        setErr(`Colour data did not load (${e.message})`);
        setResults([]);
      }
      setSearching(false);
    }, q ? 250 : 0);
    return () => clearTimeout(timer.current);
  }, [code, make, base, makeList]);

  const shown = useMemo(() => {
    if (!results) return null;
    const f = filter.trim().toLowerCase();
    const list = f ? results.filter((c) => `${c.name} ${c.code} ${(c.uses || []).join(' ')}`.toLowerCase().includes(f)) : results;
    return list.slice(0, 60);
  }, [results, filter]);

  if (!paint) {
    return (
      <s-banner tone="critical" heading="The paint product could not be read">
        <s-paragraph>Check that custom-mixed-auto-paint exists and is active.</s-paragraph>
      </s-banner>
    );
  }

  const chosen = manual
    ? { name: m.name.trim(), code: cleanCode(m.code).toUpperCase(), hex: m.hex, fx: fxForFinish(m.finish), tierOverride: m.tier, make: '' }
    : color;
  const T = chosen ? colorTiers(chosen, paint) : null;
  const system = chosen ? systemFor(paint, choice.system, T.tri) : choice.system;
  const built = chosen && (chosen.name || chosen.code) ? paintLines(chosen, { ...choice, system }, paint) : null;
  if (built?.lines) built.lines = built.lines.map((l) => (prices[l.variantId] != null ? { ...l, unitPrice: prices[l.variantId] } : l));
  const plan = built?.lines?.length
    ? addonPlan({ lines: built.lines, tiers: T, system }, paint, addons || {}, { activator: settings.activatorRatio, reducer: settings.reducerRatio, activatorProduct: settings.activatorProduct }, chosenAddon)
    : [];
  const picked = plan
    .filter((a) => (extras[a.key] ?? a.on))
    .map((a) => ({ ...a, qty: extraQty[a.key] ?? a.qty, price: prices[a.unit === 'oz' ? `oz:${a.key}` : a.variantId] ?? a.price }));
  const subtotal = round2(
    (built?.lines || []).reduce((s, l) => s + l.unitPrice * l.quantity, 0) + picked.reduce((s, a) => s + a.price * a.qty, 0),
  );
  /* clears are one-of: ticking one unticks the others */
  const tick = (a, on) => {
    const next = { ...extras, [a.key]: on };
    if (on && a.group === 'clear') for (const o of plan) if (o.group === 'clear' && o.key !== a.key) next[o.key] = false;
    setExtras(next);
  };
  const setPrice = (variantId, v) => {
    const n = parseFloat(v);
    setPrices((p) => ({ ...p, [variantId]: n >= 0 ? round2(n) : undefined }));
  };

  const add = () => {
    if (!built?.lines?.length || !chosen.code) return;
    onAdd([...built.lines, ...picked.map((a) => ({ ...addonLine(a, chosen), unitPrice: a.price }))]);
    setColor(null);
    setManual(false);
    setPrices({});
    setExtras({});
    setExtraQty({});
    setChoice((c) => ({ ...c, qty: 1, vehicle: '' }));
  };

  return (
    <s-box padding="base" background="subdued" borderRadius="base" border="base">
      <s-stack gap="base">
        <s-stack direction="inline" justifyContent="space-between" alignItems="center">
          <s-heading>Add paint</s-heading>
          <s-button variant="tertiary" icon="x" accessibilityLabel="Close" onClick={onClose} />
        </s-stack>

        {!chosen && !manual && (
          <s-stack gap="base">
            {!base && (
              <s-banner tone="warning">
                <s-paragraph>The store's colour files could not be found, so search is off. Enter the colour by hand.</s-paragraph>
              </s-banner>
            )}
            <s-grid gridTemplateColumns="1fr 220px" gap="base">
              <s-text-field
                label="Paint code"
                placeholder="e.g. 1G3, 070, WA8555, NH731P"
                value={code}
                autocomplete="off"
                onInput={(e) => setCode(e.currentTarget.value)}
              />
              <s-select label="Make" value={make} onChange={(e) => setMake(e.currentTarget.value)}>
                <s-option value="">Any make</s-option>
                {makeList.map((x) => (
                  <s-option key={x.slug} value={x.slug}>
                    {x.name}
                  </s-option>
                ))}
              </s-select>
            </s-grid>
            {!code.trim() && make && (
              <s-text-field label="Filter" labelAccessibilityVisibility="exclusive" placeholder="Filter by name, model or year" value={filter} onInput={(e) => setFilter(e.currentTarget.value)} />
            )}
            {searching && <s-spinner accessibilityLabel="Searching colours" size="base" />}
            {err && <s-text tone="critical">{err}</s-text>}
            {shown && !searching && shown.length === 0 && (
              <s-paragraph color="subdued">No colour under that code{make ? ' for this make' : ''}. Try without the make, or enter it by hand.</s-paragraph>
            )}
            {shown && shown.length > 0 && (
              <div class="results">
                <s-stack gap="none">
                  {shown.map((c) => (
                    <ColourRow key={`${c.key}|${c.name}|${c.make}`} c={c} tier={colorTiers(c, paint).base} onPick={setColor} />
                  ))}
                </s-stack>
              </div>
            )}
            <s-stack direction="inline" gap="small">
              <s-button variant="tertiary" onClick={() => { setManual(true); setM((x) => ({ ...x, code: code.trim().toUpperCase() })); }}>
                Can't find it? Enter the colour by hand
              </s-button>
            </s-stack>
          </s-stack>
        )}

        {manual && (
          <s-stack gap="base">
            <s-grid gridTemplateColumns="1fr 1fr" gap="base">
              <s-text-field label="Color name" value={m.name} onInput={(e) => setM({ ...m, name: e.currentTarget.value })} />
              <s-text-field label="Paint code" required value={m.code} onInput={(e) => setM({ ...m, code: e.currentTarget.value })}
                details="Goes on the order the counter mixes from. No code? Type what the can should say, e.g. SAMPLE." />
              <s-select label="Finish" value={m.finish} onChange={(e) => setM({ ...m, finish: e.currentTarget.value })}>
                {FINISHES.map((f) => <s-option key={f} value={f}>{f}</s-option>)}
              </s-select>
              <s-select label="Formula tier" value={m.tier} onChange={(e) => setM({ ...m, tier: e.currentTarget.value })}>
                {['Standard', paint.tierUp, paint.tierTop].filter(Boolean).map((t) => <s-option key={t} value={t}>{t}</s-option>)}
              </s-select>
            </s-grid>
            <s-color-field label="Swatch color (for the quote only)" value={m.hex} onChange={(e) => setM({ ...m, hex: e.currentTarget.value })} />
            <s-button variant="tertiary" onClick={() => setManual(false)}>Back to search</s-button>
          </s-stack>
        )}

        {chosen && (chosen.name || chosen.code) && (
          <s-stack gap="base">
            {!manual && (
              <s-stack direction="inline" gap="base" alignItems="center">
                <Swatch hex={chosen.hex} fx={chosen.fx} name={chosen.name} size="lg" />
                <s-stack gap="none">
                  <s-text type="strong">{chosen.name}</s-text>
                  <s-text color="subdued">{chosen.code}{chosen.make ? ` · ${chosen.make}` : ''} · {T.base} formula</s-text>
                </s-stack>
                <s-button variant="tertiary" onClick={() => setColor(null)}>Change</s-button>
              </s-stack>
            )}
            {T.tri && (
              <s-banner tone="info" heading="3-stage tricoat: quoted as a 2-can set">
                <s-paragraph>Stage 1 basecoat + Stage 2 pearl mid-coat, both mixed to this code. Basecoat (1K) only.</s-paragraph>
              </s-banner>
            )}
            <s-grid gridTemplateColumns="1fr 1fr 1fr" gap="base">
              <s-select label="System" value={system} disabled={T.tri} onChange={(e) => setChoice({ ...choice, system: e.currentTarget.value })}>
                {paint.systems.map((s) => <s-option key={s} value={s}>{s}{systemKind(s) ? ` (${SYSTEM_LABEL[systemKind(s)].toLowerCase()})` : ''}</s-option>)}
              </s-select>
              <s-select
                label="Size"
                value={choice.custom ? '__oz' : choice.size}
                onChange={(e) => {
                  const v = e.currentTarget.value;
                  setChoice(v === '__oz' ? { ...choice, custom: true } : { ...choice, custom: false, size: v });
                }}
              >
                {paint.sizes.map((s) => <s-option key={s} value={s}>{s}</s-option>)}
                {paint.perOz.length > 0 && <s-option value="__oz">Custom amount (per oz)</s-option>}
              </s-select>
              {choice.custom ? (
                <s-number-field label="Ounces" min={1} step={1} value={String(choice.oz)} suffix="oz" onChange={(e) => setChoice({ ...choice, oz: Math.max(1, parseInt(e.currentTarget.value, 10) || 1) })} />
              ) : (
                <s-number-field label={T.tri ? 'Sets' : 'Quantity'} min={1} step={1} value={String(choice.qty)} onChange={(e) => setChoice({ ...choice, qty: Math.max(1, parseInt(e.currentTarget.value, 10) || 1) })} />
              )}
            </s-grid>
            <s-text-field label="Vehicle (optional)" placeholder="e.g. 2019 Toyota Camry" value={choice.vehicle} onInput={(e) => setChoice({ ...choice, vehicle: e.currentTarget.value })} />
            {!T.tri && systemKind(system) && (
              <s-text color="subdued">{SYSTEM_LABEL[systemKind(system)]}: {SYSTEM_NOTE[systemKind(system)]}.</s-text>
            )}
            {built.error && <s-text tone="critical">{built.error}</s-text>}
            {built.lines.length > 0 && (
              <s-stack gap="small">
                {built.lines.map((l) => {
                  const a = Object.fromEntries(l.attributes.map((x) => [x.key, x.value]));
                  return (
                    <s-grid key={l.variantId} gridTemplateColumns="1fr 140px" gap="base" alignItems="center">
                      <s-text>
                        {l.quantity}{choice.custom ? ' oz' : ' ×'} {l.variantTitle.split(' / ').slice(1).join(' / ')}
                        {a.Component ? ` — ${a.Component.replace(/^Stage \d of 2 — /, '')}` : ''}
                        {l.unitPrice !== l.listPrice && <span class="muted"> (list {money(l.listPrice)})</span>}
                      </s-text>
                      <s-money-field
                        label={choice.custom ? 'Price per oz' : 'Price each'}
                        labelAccessibilityVisibility="exclusive"
                        value={Number(l.unitPrice).toFixed(2)}
                        onChange={(e) => setPrice(l.variantId, e.currentTarget.value)}
                      />
                    </s-grid>
                  );
                })}
              </s-stack>
            )}

            {plan.length > 0 && (
              <s-stack gap="small">
                <s-text type="strong">Complete the job</s-text>
                {plan.map((a) => {
                  const on = extras[a.key] ?? a.on;
                  const qty = extraQty[a.key] ?? a.qty;
                  const price = prices[a.unit === 'oz' ? `oz:${a.key}` : a.variantId] ?? a.price;
                  return (
                    <s-grid key={a.group} gridTemplateColumns="1fr 76px 120px" gap="base" alignItems="center">
                      <s-stack gap="small-200">
                        <s-checkbox
                          label={a.choices?.length > 1 ? a.role : `${a.role}: ${a.title}`}
                          details={a.unit === 'oz'
                          ? `${a.why} · poured from the ${a.oz === 32 ? 'quart' : `${a.oz} oz can`}, priced by the ounce`
                          : [a.why, a.oz ? `${a.oz === 32 ? 'quart' : a.oz === 128 ? 'gallon' : `${a.oz} oz`} each` : ''].filter(Boolean).join(' · ')}
                          checked={on}
                          onChange={(e) => tick(a, e.currentTarget.checked)}
                        />
                        {a.choices?.length > 1 && (
                          <s-select label={`Which ${a.role.toLowerCase()}`} labelAccessibilityVisibility="exclusive" value={a.key} disabled={!on}
                            onChange={(e) => {
                              const k = e.currentTarget.value;
                              setChosenAddon({ ...chosenAddon, [a.group]: k });
                              setExtras((x) => ({ ...x, [k]: x[a.key] ?? a.on }));
                            }}>
                            {a.choices.map((c) => <s-option key={c.key} value={c.key}>{c.title} — {money(c.price)}{c.unit === 'oz' ? '/oz' : ''}</s-option>)}
                          </s-select>
                        )}
                      </s-stack>
                      <s-number-field label={a.unit === 'oz' ? 'Ounces' : 'Qty'} labelAccessibilityVisibility="exclusive" min={1} step={1} value={String(qty)} disabled={!on}
                        suffix={a.unit === 'oz' ? 'oz' : undefined}
                        onChange={(e) => setExtraQty({ ...extraQty, [a.key]: Math.max(1, parseInt(e.currentTarget.value, 10) || 1) })} />
                      <s-money-field label={a.unit === 'oz' ? 'Price per oz' : 'Price each'} labelAccessibilityVisibility="exclusive" value={Number(price).toFixed(2)} disabled={!on}
                        details={a.unit === 'oz' ? 'per oz' : undefined}
                        onChange={(e) => setPrice(a.unit === 'oz' ? `oz:${a.key}` : a.variantId, e.currentTarget.value)} />
                    </s-grid>
                  );
                })}
              </s-stack>
            )}

            {built.lines.length > 0 && (
              <s-stack direction="inline" justifyContent="end" alignItems="center" gap="base">
                {!chosen.code && <s-text tone="critical">Type the paint code first</s-text>}
                <s-text type="strong">{money(subtotal)}</s-text>
                <s-button variant="primary" disabled={!chosen.code} onClick={add}>Add to quote</s-button>
              </s-stack>
            )}
          </s-stack>
        )}
      </s-stack>
    </s-box>
  );
}
